import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../components/ui/Button';
import { X, Play, Pause, Wind } from 'lucide-react';
interface BreathingExerciseProps {
  onComplete?: () => void;
  onSkip?: () => void;
  duration?: number;
  autoStart?: boolean;
}
export function BreathingExercise({
  onComplete,
  onSkip,
  duration = 60,
  autoStart = true
}: BreathingExerciseProps) {
  const [isActive, setIsActive] = useState(autoStart);
  const isActiveRef = useRef(isActive);
  const [phase, setPhase] = useState<'inhale' | 'hold' | 'exhale' | 'hold2'>('inhale');
  const [timeLeft, setTimeLeft] = useState(duration);
  const [text, setText] = useState('Adem in...');
  useEffect(() => {
    isActiveRef.current = isActive;
  }, [isActive]);
  useEffect(() => {
    if (!isActive) return;
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          if (onComplete) onComplete();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [isActive, onComplete]);
  useEffect(() => {
    if (!isActive) return;
    const cycleDuration = 4000;
    const timeoutIds: ReturnType<typeof setTimeout>[] = [];
    const runCycle = () => {
      setPhase('inhale');
      setText('Adem rustig in...');
      const t1 = setTimeout(() => {
        if (!isActiveRef.current) return;
        setPhase('hold');
        setText('Hou vast...');
        const t2 = setTimeout(() => {
          if (!isActiveRef.current) return;
          setPhase('exhale');
          setText('Blaas langzaam uit...');
          const t3 = setTimeout(() => {
            if (!isActiveRef.current) return;
            setPhase('hold2');
            setText('Even wachten...');
          }, cycleDuration);
          timeoutIds.push(t3);
        }, cycleDuration);
        timeoutIds.push(t2);
      }, cycleDuration);
      timeoutIds.push(t1);
    };
    runCycle();
    const interval = setInterval(runCycle, cycleDuration * 4);
    return () => {
      clearInterval(interval);
      timeoutIds.forEach(id => clearTimeout(id));
    };
  }, [isActive]);
  const toggleActive = () => setIsActive(!isActive);
  const circleVariants = {
    inhale: {
      scale: 1.5,
      opacity: 1,
      backgroundColor: '#818cf8'
    },
    hold: {
      scale: 1.5,
      opacity: 0.8,
      backgroundColor: '#6366f1'
    },
    exhale: {
      scale: 1.0,
      opacity: 0.6,
      backgroundColor: '#a5b4fc'
    },
    hold2: {
      scale: 1.0,
      opacity: 0.4,
      backgroundColor: '#c7d2fe'
    }
  };
  return <div className="flex flex-col items-center justify-center p-8 bg-slate-50/50 rounded-3xl border border-slate-100 max-w-md mx-auto relative overflow-hidden" data-id="element-1008">
      <div className="absolute inset-0 bg-gradient-to-b from-orange-50/50 to-white pointer-events-none" data-id="element-1009" />
      <div className="relative z-10 w-full flex flex-col items-center" data-id="element-1010">
        <div className="flex justify-between w-full mb-8 items-center" data-id="element-1011">
          <div className="flex items-center gap-2 text-orange-700 font-medium" data-id="element-1012">
            <Wind className="w-5 h-5" data-id="element-1013" />
            <span data-id="element-1014">Even rustig worden</span>
          </div>
          {onSkip && <button onClick={onSkip} className="text-slate-400 hover:text-slate-600 transition-colors p-2" aria-label="Overslaan" data-id="element-1015">
              <X className="w-5 h-5" data-id="element-1016" />
            </button>}
        </div>

        <div className="relative w-64 h-64 flex items-center justify-center mb-8" data-id="element-1017">
          <motion.div className="absolute inset-0 rounded-full border-2 border-orange-100" animate={{
          scale: phase === 'inhale' || phase === 'hold' ? 1.2 : 1
        }} transition={{
          duration: 4,
          ease: 'easeInOut'
        }} data-id="element-1018" />
          <motion.div className="absolute inset-4 rounded-full border-2 border-orange-200" animate={{
          scale: phase === 'inhale' || phase === 'hold' ? 1.1 : 1
        }} transition={{
          duration: 4,
          ease: 'easeInOut'
        }} data-id="element-1019" />
          <motion.div className="w-32 h-32 rounded-full shadow-lg backdrop-blur-sm flex items-center justify-center" variants={circleVariants} animate={isActive ? phase : 'exhale'} transition={{
          duration: 4,
          ease: 'easeInOut'
        }} data-id="element-1020">
            <span className="text-white font-bold text-lg" data-id="element-1021">
              {isActive ? phase === 'inhale' ? 'In' : phase === 'exhale' ? 'Uit' : '...' : 'Start'}
            </span>
          </motion.div>
        </div>

        <h3 className="text-2xl font-bold text-slate-800 mb-2 h-8 transition-all duration-500" data-id="element-1022">
          {isActive ? text : 'Klaar om te beginnen?'}
        </h3>
        <p className="text-slate-500 mb-8 text-center max-w-xs" data-id="element-1023">
          Concentreer je op de cirkel en adem rustig mee. Dit helpt je hersenen
          om zich te focussen.
        </p>

        <div className="flex gap-4 w-full" data-id="element-1024">
          <Button onClick={toggleActive} variant={isActive ? 'secondary' : 'primary'} className="flex-1" data-id="element-1025">
            {isActive ? <>
                <Pause className="w-4 h-4 mr-2" data-id="element-1026" /> Pauze
              </> : <>
                <Play className="w-4 h-4 mr-2" data-id="element-1027" /> Start
              </>}
          </Button>
          {onComplete && <Button onClick={onComplete} variant="ghost" className="flex-1" data-id="element-1028">
              Ik ben er klaar voor
            </Button>}
        </div>
      </div>
    </div>;
}